'use client';

import {
  Box,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { Suspense, useMemo } from 'react';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

import Logo from '../Logo';
import AppStore from '@/_assets/images/app_store.png';
import GooglePlay from '@/_assets/images/google_play.png';
import { IRedux, SocialMediaLinks } from '../../_store/types';
import { getPage } from '../../_utils/PagesList';
import { useQuery } from '@tanstack/react-query';
import getShipperById from '../../_service/shipper.service';
import {
  FacebookGrayModeIcon,
  InstagramGrayModeIcon,
  TelegramGrayModeIcon,
  WhatsUpGrayModeIcon,
  YoutubeGrayModeIcon,
} from '@/_assets/icon-components';
import CopyrightBar from './_components/CopyrightBar';
import { formatPhoneNumber } from '../../_utils/formatPhoneNumber';

const Desktop = () => {
  const settings = useSelector((state: IRedux) => state.settings);
  const t = useTranslations();

  const { firstColumn, secondColumn } = useMemo(() => {
    const pages =
      settings?.data?.data?.pages?.filter(
        (item) => item?.value !== 'my_address'
      ) || [];
    const midIndex = Math.ceil(pages.length / 2);

    return {
      firstColumn: pages.slice(0, midIndex),
      secondColumn: pages.slice(midIndex),
    };
  }, [settings?.data?.data?.pages]);

  const { data: shipper } = useQuery({
    queryKey: ['shipper-by-id', settings?.data?.shipper_id],
    queryFn: () =>
      getShipperById(settings?.data?.shipper_id || '').then((res) => res.data),
    enabled: Boolean(settings?.data?.shipper_id),
  });

  const social_media = settings?.data?.data?.social_media;

  // Social media icons in the order shown in the image: YouTube, Instagram, Facebook, Telegram
  const socialMedias = [
    {
      id: 1,
      icon: <YoutubeGrayModeIcon />,
      value: 'youtube_link',
    },
    {
      id: 2,
      icon: <InstagramGrayModeIcon />,
      value: 'instagram_link',
    },
    {
      id: 3,
      icon: <FacebookGrayModeIcon />,
      value: 'facebook_link',
    },
    {
      id: 4,
      icon: <TelegramGrayModeIcon />,
      value: 'tg_link',
    },
  ] as {
    id: number;
    icon: React.ReactNode;
    value: keyof SocialMediaLinks;
  }[];

  const filteredSocialMedias = socialMedias
    .filter((item) => social_media?.[item.value])
    .map((item) => ({
      ...item,
      link: social_media?.[item.value],
    }));

  const formattedPhoneNumber = formatPhoneNumber(shipper?.phone?.[0] || '');

  return (
    <Box bg='#000' color='white' px={{ base: 4, lg: 14 }}>
      <SimpleGrid columns={4} gap={10} py='64px'>
        <Stack gap={4}>
          <Logo yapona />
          {(social_media?.app_store_link ||
            social_media?.play_store_link) && (
              <Text fontSize='sm' fontWeight={400} color='white'>
                {t('download_at')}
              </Text>
            )}
          <Stack gap={3}>
            <Suspense fallback=''>
              {social_media?.app_store_link && (
                <Link href={social_media?.app_store_link}>
                  <Image
                    width={135}
                    height={40}
                    src={AppStore}
                    alt='app store'
                  />
                </Link>
              )}
              {social_media?.play_store_link && (
                <Link href={social_media?.play_store_link}>
                  <Image
                    width={135}
                    height={40}
                    src={GooglePlay}
                    alt='google play'
                  />
                </Link>
              )}
            </Suspense>
          </Stack>
        </Stack>

        <Stack gap={3}>
          {firstColumn.map((item) => {
            const page = getPage(item.value);
            return (
              <Box fontWeight={600} color='#FFFFFFB2' key={item?.value}>
                <Link href={page.route}>
                  {page?.type === 'dynamic' ? item?.label : t(page.title)}
                </Link>
              </Box>
            );
          })}
        </Stack>

        <Stack gap={3}>
          {secondColumn.map((item) => {
            const page = getPage(item.value);
            return (
              <Box fontWeight={600} color='#FFFFFFB2' key={item?.value}>
                <Link href={page.route}>
                  {page?.type === 'dynamic' ? item?.label : t(page.title)}
                </Link>
              </Box>
            );
          })}
          <Box fontWeight={600} color='#FFFFFFB2'>
            <Link href={'/visiting-rules'}>
              {t('visiting_rules')}
            </Link>
          </Box>
          <Box fontWeight={600} color='#FFFFFFB2'>
            <Link href={'/public-offer'}>
              {t('public_offer')}
            </Link>
          </Box>
          <Box fontWeight={600} color='#FFFFFFB2'>
            <Link href={'/loyaltyoffer'}>
              {t('loyalty_offer')}
            </Link>
          </Box>
        </Stack>

        <Stack gap={6} alignItems={'flex-end'}>
          {shipper?.phone?.[0] && (
            <Text
              color='white'
              fontSize={24}
              fontWeight={600}
              lineHeight={'24px'}
            >
              <Link
                href={`tel:${shipper?.phone?.[0]}`}
                style={{ color: 'white' }}
              >
                {formattedPhoneNumber}
              </Link>
            </Text>
          )}
          {shipper?.work_hour_start && shipper?.work_hour_end && (
            <Text fontWeight={500} color='white' lineHeight={'20px'}>
              {t('from_to', {
                from: shipper?.work_hour_start,
                to: shipper?.work_hour_end,
              })}
            </Text>
          )}
          <HStack gap={6}>
            {filteredSocialMedias?.map(
              (el) =>
                el.link && (
                  <Link
                    href={el.link}
                    key={el.id}
                    target='_blank'
                    style={{ color: 'white' }}
                  >
                    {el.icon}
                  </Link>
                )
            )}
          </HStack>
        </Stack>
      </SimpleGrid>
      <CopyrightBar />
    </Box>
  );
};

export default Desktop;
